import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Wallet, Gamepad2, Receipt, ArrowRight, Cpu } from 'lucide-react';

const TYPE_COLORS = {
  purchase: 'bg-primary/15 text-primary border-primary/30',
  purchase_system: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  repair: 'bg-secondary/15 text-secondary border-secondary/30',
  transport: 'bg-muted text-muted-foreground border-border',
};

const BAR_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-lime-500'];

const Investors = () => {
  const { t, API } = useApp();
  const navigate = useNavigate();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvestors = async () => {
      try {
        const res = await axios.get(`${API}/investors`);
        setInvestors(res.data);
      } catch (e) {
        console.error('Failed to fetch investors:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInvestors();
  }, [API]);

  const formatCurrency = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const grandTotal = investors.reduce((s, i) => s + (i.total_invested || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="investors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('investors.title')}
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-xs">{t('investors.subtitle')}</p>
        </div>
        <Card className="bezel border-primary/25">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center glow-amber">
              <Wallet className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="eyebrow text-[10px] text-muted-foreground">{t('investors.totalInvested')}</p>
              <p className="font-display text-2xl font-bold tabular-nums text-emerald-400">{formatCurrency(grandTotal)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {investors.length === 0 ? (
        <Card className="bezel p-12 text-center">
          <Wallet className="w-16 h-16 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground">{t('investors.noData')}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {investors.map((inv, index) => (
            <Card
              key={inv.id}
              className="bezel hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/investors/${encodeURIComponent(inv.id)}`)}
              data-testid={`investor-card-${inv.id}`}
            >
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-xs font-mono text-muted-foreground w-5 shrink-0">#{index + 1}</div>
                    <Avatar className="w-11 h-11">
                      <AvatarImage src={inv.avatar || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold">
                        {inv.nickname?.slice(0, 2).toUpperCase() || '??'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{inv.nickname}</p>
                      <p className="text-xs text-muted-foreground font-mono flex items-center gap-2">
                        <span className="flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> {inv.cabinets_count}</span>
                        <span className="flex items-center gap-1"><Receipt className="w-3 h-3" /> {inv.operations_count}</span>
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>

                {/* Montant + part */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xl font-display font-bold tabular-nums text-primary">{formatCurrency(inv.total_invested)}</span>
                    <span className="text-sm font-mono text-muted-foreground tabular-nums">{inv.percentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${index === 0 ? 'bg-primary glow-amber' : BAR_COLORS[index % BAR_COLORS.length]}`}
                      style={{ width: `${Math.min(inv.percentage, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Répartition par type */}
                {inv.by_type && Object.keys(inv.by_type).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(inv.by_type).map(([type, amount]) => (
                      <Badge key={type} variant="outline" className={`text-[11px] font-mono ${TYPE_COLORS[type] || ''}`}>
                        {t(`operations.${type}`)}: {formatCurrency(amount)}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Participation pièces détachées (indicatif, hors total) */}
                {inv.spare_total > 0 && (
                  <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5 pt-1 border-t border-border/50" title={t('investors.spareParticipationNote')}>
                    <Cpu className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                    {t('investors.spareParticipationShort')}: <span className="text-foreground">{formatCurrency(inv.spare_total)}</span>
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Investors;
